import React from 'react';
import PropTypes from 'prop-types';
import Draw from 'ol/interaction/Draw';
import Map from 'ol/Map';
import {
  addFeaturesToLayerUsingGeojsonGeometry,
  clearMapLayer,
  createLayer,
} from 'utils/map/layers';
import { View } from 'ol';
import {
  createLayerForJurisdiction,
  getJurisdictionExtent,
} from 'components/map/mapUtils';
import TileLayer from 'ol/layer/Tile';
import { OSM, Vector } from 'ol/source';
import { addLayerToMap, getGeojsonGeometryFromFeature } from 'utils/map/map';
import Icons from 'consts/icons';
import FormHelperText from '@material-ui/core/FormHelperText';
import { useSelector } from 'react-redux';
import KML from 'ol/format/KML';
import { fromCircle } from 'ol/geom/Polygon';
import { toLonLat } from 'ol/proj';
import Tooltip from '@material-ui/core/Tooltip';
import ButtonsUseStyles from 'consts/styles/Buttons';
import { formStyles } from 'consts/styles/Form';
import { mapUseStyles } from './mapUseStyles';
import ObjectsRender from './ObjectsRender';

const MapToDraw = props => {
  const { geojson, setGeojson, drawTypes, setErrors, errors } = props;

  const strings = useSelector(state => state.language).areas.add.map;
  const { placeholderForm } = useSelector(state => state.language).areas;

  const map = React.useRef(null);
  const layer = React.useRef(null);
  const draw = React.useRef(null);
  const raster = React.useRef(
    new TileLayer({
      source: new OSM(),
    })
  );
  const classes = mapUseStyles();
  const classesButtons = ButtonsUseStyles();
  const formClasses = formStyles();

  const [active, setActive] = React.useState(null);

  const mapInit = () => {
    layer.current = createLayer('drawingsLayer');
    map.current = new Map({
      layers: [raster.current],
      target: 'mapToDrawApprovedAreas',
      view: new View({
        center: [-11000000, 4600000],
        zoom: 4,
      }),
    });
    map.current.addLayer(createLayerForJurisdiction());
    addLayerToMap(map.current, layer.current);
    const areaToCenterView = getJurisdictionExtent();
    map.current.getView().fit(areaToCenterView);
  };
  React.useEffect(mapInit, []);

  const addInteraction = typeValue => {
    setActive(typeValue);
    map.current.removeInteraction(draw.current);
    clearMapLayer(layer.current);
    if (typeValue !== 'None') {
      if (setErrors) setErrors({ ...errors, map: false });
      draw.current = new Draw({
        source: new Vector(),
        type: typeValue,
      });
      map.current.addInteraction(draw.current);
      draw.current.on('drawend', event => {
        handleDrawEnd(event.feature, typeValue);
      });
    } else {
      if (setErrors) setErrors({ ...errors, map: true });
      setGeojson(null);
      clearMapLayer(layer.current);
    }
  };

  React.useEffect(() => {
    addFeaturesToLayerUsingGeojsonGeometry(layer.current, geojson);
  }, [geojson]);

  const handleDrawEnd = (feature, typeValue) => {
    if (typeValue === 'Circle') {
      const circleAsPolygon = fromCircle(feature.getGeometry(), 64, 0);
      const circleGeojsonObject = {
        type: 'Polygon',
        coordinates: [
          parseFlatCoordinatesForCircle(circleAsPolygon.flatCoordinates),
        ],
      };
      setGeojson(circleGeojsonObject);
    } else {
      setGeojson(getGeojsonGeometryFromFeature(feature));
    }
    map.current.removeInteraction(draw.current);
  };

  const parseFlatCoordinatesForCircle = circleArr => {
    const returnedArray = [];
    for (let i = 0; i < circleArr.length; i += 1) {
      if (i < circleArr.length - 1) {
        const firstElem = circleArr[i];
        const secondElem = circleArr[i + 1];
        const eachArr = toLonLat([firstElem, secondElem], 'EPSG:3857');
        returnedArray.push(eachArr);
        i += 1;
      }
    }

    return returnedArray;
  };

  const uploadFile = e => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        const shapeSource = new Vector({
          url: reader.result,
          format: new KML(),
        });

        layer.current.setSource(shapeSource);
        shapeSource.on('change', evt => {
          const source = evt.target;
          if (source.getState() === 'ready') {
            const renderedFeatures = source.getFeatures();
            setGeojson(getGeojsonGeometryFromFeature(renderedFeatures[0]));
          }
        });
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className={classes.DrawingApprovedAreasMap}>
      <div className={`${classes.DrawingApprovedAreasMap}__icons-wrapper`}>
        {drawTypes.includes('polygon') && (
          <Tooltip title={strings.polygon_} placement="right">
            <Icons.Actions.Edit2
              className={`${classes.DrawingApprovedAreasMap}__map-icon ${
                active === 'Polygon'
                  ? `${classes.DrawingApprovedAreasMap}__map-icon--active`
                  : ''
              } ${classesButtons.ButtonsCircle}--mid`}
              onClick={() => addInteraction('Polygon')}
            />
          </Tooltip>
        )}
        {drawTypes.includes('line') && (
          <Tooltip title={strings.line_} placement="right">
            <Icons.Misc.Timeline
              className={`${classes.DrawingApprovedAreasMap}__map-icon ${
                active === 'LineString'
                  ? `${classes.DrawingApprovedAreasMap}__map-icon--active`
                  : ''
              } ${classesButtons.ButtonsCircle}--mid`}
              onClick={() => addInteraction('LineString')}
            />
          </Tooltip>
        )}
        {drawTypes.includes('circle') && (
          <Tooltip title={strings.circle_} placement="right">
            <Icons.Misc.RadioUnchecked
              className={`${classes.DrawingApprovedAreasMap}__map-icon ${
                active === 'Circle'
                  ? `${classes.DrawingApprovedAreasMap}__map-icon--active`
                  : ''
              } ${classesButtons.ButtonsCircle}--mid`}
              onClick={() => addInteraction('Circle')}
            />
          </Tooltip>
        )}

        <label htmlFor="readFileInput">
          <input
            className={`${classes.DrawingApprovedAreasMap}__fileInput ${classes.DrawingApprovedAreasMap}__map-icon--select-file`}
            type="file"
            onChange={e => uploadFile(e)}
            id="readFileInput"
            accept=".kml"
          />
          <Tooltip title={strings.selectFile_} placement="right">
            <Icons.Actions.SearchFile
              className={`${classes.DrawingApprovedAreasMap}__map-icon  ${classesButtons.ButtonsCircle}--mid`}
            />
          </Tooltip>
        </label>
        <Tooltip title={strings.clear_} placement="right">
          <Icons.Actions.HighlightOff
            className={`${classes.DrawingApprovedAreasMap}__map-icon
               ${classesButtons.ButtonsCircle}--mid`}
            onClick={() => addInteraction('None')}
          />
        </Tooltip>
      </div>

      <ObjectsRender map={map} />

      <div
        className={`${classes.DrawingApprovedAreasMap}__map-wrapper__inner`}
        id="mapToDrawApprovedAreas"
      ></div>
      {errors?.map !== undefined ? (
        <FormHelperText
          className={`${formClasses.Form}__label ${
            errors.map
              ? ` ${formClasses.Form}--bottom-of-container ${formClasses.Form}__text-field__form-helper--error`
              : ''
          }`}
        >
          {errors.map ? placeholderForm.required : null}
        </FormHelperText>
      ) : null}
    </div>
  );
};

MapToDraw.propTypes = {
  geojson: PropTypes.object,
  setGeojson: PropTypes.func.isRequired,
  drawTypes: PropTypes.array,
  errors: PropTypes.object,
  setErrors: PropTypes.func,
};

export default MapToDraw;
